import { Icon } from "@iconify/react";
import {
  Close,
  LocalMallRounded,
  MenuOpen,
  ShoppingCartRounded,
} from "@mui/icons-material";
import {
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  SwipeableDrawer,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as cartReducer from "../../../../redux/cart/cart.reducers";
import {
  AppDispatch,
  RootState,
  useAppDispatch,
} from "../../../../redux/store";
import * as userReducer from "../../../../redux/users/users.reducers";
import brand, { primary, secondary } from "../../../../themes/theme";
import DeleteItemDialog from "../../../../utils/common/delete-item-dialog/DeleteItemDialog";
import { TokenUtil } from "../../../../utils/TokenUtil";
import ProfileDrawerContent from "../../components/proflle-menu-drawer/ProfileDrawer";
import "./MainNavBar.css";

export default function MainNavBar() {
  const dispatch: AppDispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeNavItem, setActiveNavItem] = useState<string>("");
  const [logoutDialogOpen, setLogoutDialogOpen] = useState<boolean>(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openProfileDrawer, setOpenProfileDrawer] = useState(false);

  const handleDrawerOpen = () => {
    setOpenDrawer(true);
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
  };

  const handleProfileDrawerOpen = () => {
    setOpenProfileDrawer(true);
  };

  const handleProfileDrawerClose = () => {
    setOpenProfileDrawer(false);
  };

  const [profileState, setProfileState] = React.useState({
    top: false,
    left: false,
    bottom: false,
    right: false,
  });

  const toggleDrawer =
    (anchor: string, openDrawer: boolean) =>
    (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event &&
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }

      setProfileState({ ...profileState, [anchor]: openDrawer });
      handleDrawerClose();
      handleProfileDrawerClose();
    };

  // Get Data From Redux
  const userState: userReducer.InitialState = useSelector(
    (state: RootState) => {
      return state[userReducer.userFeatureKey];
    }
  );

  // Get Redux Cart Info
  const cartReduxState: cartReducer.InitialState = useSelector(
    (state: RootState) => {
      return state[cartReducer.cartFeatureKey];
    }
  );

  const { user, isAuthenticated } = userState;
  const { cartProduct } = cartReduxState;

  const gradientBorderClass = user.isSuperAdmin
    ? "gradient-border super-admin"
    : user.isAdmin
    ? "gradient-border admin"
    : "gradient-border";

  useEffect(() => {
    // Extract the path from location to determine the active nav item
    const path = location.pathname;
    if (path.startsWith("/products/fashion")) {
      setActiveNavItem("fashion");
    } else if (path.startsWith("/products/electronics")) {
      setActiveNavItem("electronics");
    } else if (path.startsWith("/products/household")) {
      setActiveNavItem("household");
    } else if (path.startsWith("/categories/add")) {
      setActiveNavItem("admin");
    } else if (path.startsWith("/products/upload")) {
      setActiveNavItem("admin");
    } else if (path.startsWith("/products/admin")) {
      setActiveNavItem("admin");
    } else if (path.startsWith("/orders/admin")) {
      setActiveNavItem("admin");
    } else if (path.startsWith("/cart/list")) {
      setActiveNavItem("cart");
    } else {
      setActiveNavItem("");
    }
  }, [location.pathname]);

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    dispatch({
      type: `${userReducer.userLogOutAction}`,
    });
    setLogoutDialogOpen(false);
    navigate("/");
  };

  const handleDeleteCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleNavItemClick = (navItem: string, path: string) => {
    setActiveNavItem(navItem);
    navigate(path);
  };

  // Styles
  const btnStyle = {
    textTransform: "capitalize",
    color: secondary[700],
    background: "transparent",
    fontFamily: "Ubuntu",
    mx: 1,
    cursor: "pointer",
    fontWeight: "550",
    borderRadius: "10px",
    mb: 1,
    "&:hover": {
      background: secondary[100],
      color: secondary[800],
    },
  };

  const activeBtnStyle = {
    ...btnStyle,
    color: primary[900],
    background: secondary[200],
    fontWeight: "bold",
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* <AppBar position="fixed" sx={{ background: "#000048", color: "white" }}></AppBar> */}
      <AppBar
        elevation={0}
        sx={{
          background:
            TokenUtil.isLoggedIn() && isAuthenticated && user
              ? secondary[100]
              : secondary[100],
          backdropFilter: "blur(60px)",
          color:
            TokenUtil.isLoggedIn() && isAuthenticated && user
              ? brand[900]
              : brand[900],
          py: "7px",
          position: {
            xs: "static",
            sm: "static",
            md: "fixed",
          },
        }}
      >
        <Toolbar>
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            {TokenUtil.isLoggedIn() &&
              isAuthenticated &&
              user &&
              Object.keys(user).length > 0 && (
                <IconButton
                  size="large"
                  aria-label="show more"
                  aria-haspopup="true"
                  onClick={handleDrawerOpen}
                  color="inherit"
                  sx={{
                    background: "transparent",
                    color: secondary[600],
                    mt: "2px",
                    "&:hover": {
                      background: "transparent",
                      color: secondary[800],
                    },
                  }}
                >
                  <MenuOpen />
                </IconButton>
              )}
          </Box>
          <Box
            onClick={() => navigate("/")}
            sx={{
              display: {
                xs: "flex",
                sm: "flex",
              },
              width:
                TokenUtil.isLoggedIn() && isAuthenticated && user
                  ? "none"
                  : "100%",
              justifyContent: "center",
              height: "100%",
              alignItems: "center",
              color:
                TokenUtil.isLoggedIn() && isAuthenticated && user
                  ? brand[900]
                  : brand[900],
              cursor: "pointer",
            }}
          >
            <LocalMallRounded fontSize="small" sx={{ mr: "5px", mb: "2px" }} />
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                height: "100%",
                alignItems: "center",
                fontWeight: "bold",
                fontFamily: "Poppins",
                letterSpacing: 1,
              }}
            >
              hum
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }} />
          {TokenUtil.isLoggedIn() &&
            isAuthenticated &&
            user &&
            Object.keys(user).length > 0 && (
              <Box sx={{ display: { xs: "none", md: "flex" } }}>
                <Button
                  onClick={() =>
                    handleNavItemClick("fashion", "/products/fashion")
                  }
                  sx={activeNavItem === "fashion" ? activeBtnStyle : btnStyle}
                  variant="text"
                >
                  Fashion
                </Button>
                <Button
                  onClick={() =>
                    handleNavItemClick("electronics", "/products/electronics")
                  }
                  sx={
                    activeNavItem === "electronics" ? activeBtnStyle : btnStyle
                  }
                  variant="text"
                >
                  Electronics
                </Button>
                <Button
                  onClick={() =>
                    handleNavItemClick("household", "/products/household")
                  }
                  sx={activeNavItem === "household" ? activeBtnStyle : btnStyle}
                  variant="text"
                >
                  Household
                </Button>
              </Box>
            )}

          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: { xs: "flex", md: "flex" } }}>
            {TokenUtil.isLoggedIn() &&
              isAuthenticated &&
              user &&
              Object.keys(user).length > 0 && (
                <>
                  {/* <IconButton
                    size="large"
                    color="inherit"
                    sx={{ color: "grey" }}
                  >
                    <Search />
                  </IconButton> */}
                  <IconButton
                    size="large"
                    onClick={() => {
                      handleNavItemClick("cart", "/cart/list");
                    }}
                    sx={{
                      color:
                        activeNavItem === "cart" ? "#000048" : secondary[600],
                      background: "transparent",
                      "&:hover": {
                        background: "transparent",
                        color: secondary[800],
                      },
                    }}
                  >
                    <Badge
                      badgeContent={cartProduct?.products?.length}
                      sx={{
                        "& .MuiBadge-badge": {
                          backgroundColor: "orangered",
                          color: "white",
                          fontWeight: "bold",
                          fontFamily: "monospace",
                        },
                      }}
                    >
                      <ShoppingCartRounded />
                    </Badge>
                  </IconButton>
                  <Box>
                    <IconButton
                      onClick={handleProfileDrawerOpen}
                      sx={{
                        ...btnStyle,
                        display: "flex",
                        height: "100%",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                        mr: { xs: 0, sm: 1 },
                        pr: { xs: 0, sm: 1 },

                        "&:hover": {
                          background: "transparent",
                        },
                      }}
                      aria-label="account of current user"
                      aria-haspopup="true"
                      color="inherit"
                    >
                      <div className={gradientBorderClass}>
                        <img src={user?.imageUrl} alt="" />
                      </div>
                    </IconButton>
                  </Box>
                </>
              )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Logout Dialog  */}
      <DeleteItemDialog
        open={logoutDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Logout?"
        contentText="Are you sure you want to Logout?"
        confirmText="Yes"
        cancelText="No"
      />

      {/* Mini Screen Drawer  */}
      <SwipeableDrawer
        anchor="left"
        open={openDrawer}
        onClose={toggleDrawer("left", false)}
        onOpen={toggleDrawer("left", true)}
        sx={{
          zIndex: 1300,
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: { xs: 300, sm: 370, md: 300, lg: 300 },
            background: secondary[50],
          },
        }}
        PaperProps={{
          sx: { borderRadius: "0px 5px 5px 0px" },
        }}
      >
        <Box>
          {TokenUtil.isLoggedIn() &&
            isAuthenticated &&
            user &&
            Object.keys(user).length > 0 && (
              <Box sx={{ p: 1, m: 1 }}>
                <Box
                  sx={{
                    mt: 1,
                    mx: "auto",
                    display: "flex",
                    justifyContent: "space-between",
                    height: "100%",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "22px",
                      fontFamily: "Ubuntu",
                      fontWeight: "bold",
                    }}
                  >
                    Categories
                  </Typography>
                  <IconButton
                    onClick={handleDrawerClose}
                    sx={{
                      background: "transparent",
                      color: "grey",
                      "&:hover": {
                        background: "transparent",
                        color: primary[900],
                      },
                    }}
                  >
                    <Close />
                  </IconButton>
                </Box>
                <Divider sx={{ my: 1, border: "1px dashed lightgrey" }} />
                <List>
                  <ListItem
                    onClick={() =>
                      handleNavItemClick("fashion", "/products/fashion")
                    }
                    sx={activeNavItem === "fashion" ? activeBtnStyle : btnStyle}
                  >
                    <ListItemIcon>
                      <Icon
                        icon="fluent-emoji-flat:t-shirt"
                        width="28"
                        height="28"
                      />
                    </ListItemIcon>
                    <ListItemText>Fashion</ListItemText>
                  </ListItem>
                  <ListItem
                    onClick={() =>
                      handleNavItemClick("household", "/products/household")
                    }
                    sx={
                      activeNavItem === "household" ? activeBtnStyle : btnStyle
                    }
                  >
                    <ListItemIcon>
                      <Icon
                        icon="emojione-v1:house-with-garden"
                        width="28"
                        height="28"
                      />
                    </ListItemIcon>
                    <ListItemText>Household</ListItemText>
                  </ListItem>
                  <ListItem
                    onClick={() =>
                      handleNavItemClick("electronics", "/products/electronics")
                    }
                    sx={
                      activeNavItem === "electronics"
                        ? activeBtnStyle
                        : btnStyle
                    }
                  >
                    <ListItemIcon>
                      <Icon
                        icon="flat-color-icons:electronics"
                        width="28"
                        height="28"
                      />
                    </ListItemIcon>
                    <ListItemText>Electronics</ListItemText>
                  </ListItem>
                </List>
              </Box>
            )}
        </Box>
      </SwipeableDrawer>

      {/* Profile Menu Drawer  */}
      <SwipeableDrawer
        anchor="right"
        open={openProfileDrawer}
        onClose={toggleDrawer("right", false)}
        onOpen={toggleDrawer("right", true)}
        sx={{
          zIndex: 1300,
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: { xs: 330 },
            background: secondary[50],
          },
        }}
        PaperProps={{
          sx: { borderRadius: "0px 0px 0px 0px" },
        }}
      >
        <Box>
          <ProfileDrawerContent
            user={user}
            handleProfileDrawerClose={handleProfileDrawerClose}
            handleLogout={handleLogout}
            isAuthenticated={isAuthenticated}
            TokenUtil={TokenUtil}
          />
        </Box>
      </SwipeableDrawer>
    </Box>
  );
}
